import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    // Also clear any pending impersonation backup to avoid stale state.
    localStorage.removeItem('admin_backup_token');
    try { sessionStorage.removeItem('safety_warning_seen'); } catch { /* ignore */ }
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Store userId for localStorage score tracking
      localStorage.setItem('userId', response.data.id);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    // Reset per-login flags so one-time popups (e.g. safety disclaimer) show once per login.
    try { sessionStorage.removeItem('safety_warning_seen'); } catch { /* ignore */ }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    // Fetch full user data including is_admin
    const meResponse = await axios.get(`${API}/auth/me`);
    setUser(meResponse.data);
    // Store userId for localStorage score tracking
    localStorage.setItem('userId', meResponse.data.id);
    // Best-effort analytics: log the login event
    try { axios.post(`${API}/analytics/event`, { event_type: 'login' }); } catch { /* ignore */ }
    return meResponse.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    // Auto-approved flow: response includes { access_token, user } — log the user in immediately.
    const { access_token, user: newUser } = response.data;
    if (access_token) {
      localStorage.setItem('token', access_token);
      try { sessionStorage.removeItem('safety_warning_seen'); } catch { /* ignore */ }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      if (newUser) {
        setUser(newUser);
        if (newUser.id) localStorage.setItem('userId', newUser.id);
      }
    }
    return response.data;
  };

  // Admin "Ouvrir sa séance": issues a 2h token bound to the patient so
  // every exercise/challenge is stored in the PATIENT's history. The admin's
  // original token is backed up to localStorage and restored by endImpersonation.
  const startImpersonation = async (patientUserId) => {
    const response = await axios.post(`${API}/admin/users/${patientUserId}/impersonate`);
    const { access_token, user: patientUser } = response.data;
    // Keep the admin token so we can restore it later. Use the CURRENT axios
    // Authorization header as the source of truth (same value as localStorage).
    const currentAdminToken = localStorage.getItem('token');
    if (currentAdminToken) {
      localStorage.setItem('admin_backup_token', currentAdminToken);
    }
    localStorage.setItem('token', access_token);
    localStorage.setItem('userId', patientUser.id);
    try { sessionStorage.removeItem('safety_warning_seen'); } catch { /* ignore */ }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(patientUser);
    return patientUser;
  };

  const endImpersonation = async () => {
    // Best-effort audit log — never let a network glitch block the restore.
    try {
      await axios.post(`${API}/admin/impersonate/end`);
    } catch { /* ignore */ }
    const backup = localStorage.getItem('admin_backup_token');
    localStorage.removeItem('admin_backup_token');
    if (!backup) {
      // No backup → safest fallback is a full logout.
      logout();
      return null;
    }
    localStorage.setItem('token', backup);
    axios.defaults.headers.common['Authorization'] = `Bearer ${backup}`;
    setToken(backup);
    try { sessionStorage.removeItem('safety_warning_seen'); } catch { /* ignore */ }
    // Refresh user so banner disappears and admin rights are back.
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      if (me.data?.id) localStorage.setItem('userId', me.data.id);
      return me.data;
    } catch {
      logout();
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setUser, refreshUser: fetchUser, startImpersonation, endImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};
